import React, { useMemo } from "react";

import { Link, useLocation, useRouteLoaderData } from "@remix-run/react";
import {
    Activity,
    BookOpen,
    CheckSquare,
    ChevronLeft,
    ChevronRight,
    Dot,
    FileText,
    Filter,
    Gift,
    Headphones,
    HelpCircle,
    MessageCircle,
    MessageSquare,
    Mic,
    Plus,
    Shield,
    SortAsc,
    SortDesc,
    Shapes,
    UserPlus,
    Users,
} from "lucide-react";

// Add SessionsHeader component
import { Button } from "~/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/ui/tooltip";
import { A, H2 } from "~/components/ui/typography";
import { UserMenu } from "~/components/user-menu";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { useRecoverableRecordings } from "~/hooks/media/use-recoverable-recordings";
import { useAnonymous } from "~/hooks/use-anonymous";
import { cn } from "~/lib/utils";
import { SessionUsageSidebarCard } from "~/components/billing/session-usage-sidebar-card";

import { useIsPractitioner } from "../lib/user";
import { useHwaPromoPip } from "~/features/hwa-access/hooks/use-hwa-promo-pip";

import Logo from "./logo";

interface NavItemData {
    href: string;
    icon: React.ReactNode;
    text: string;
    badge?: number;
    /** AIDEV-NOTE: Orange attention pip (e.g. unfinished recording waiting for retry) */
    attention?: boolean;
}

interface NavItemProps extends NavItemData {
    isActive: boolean;
    disabled?: boolean;
    collapsed?: boolean;
    "data-tour-id"?: string;
}

const NavItem = ({
    href,
    isActive,
    icon,
    text,
    badge,
    attention,
    disabled,
    collapsed,
    "data-tour-id": dataTourId,
}: NavItemProps) => {
    // AIDEV-NOTE: When collapsed, show only icon with tooltip
    if (collapsed) {
        const content = (
            <div
                className={cn(
                    "flex size-11 items-center justify-center rounded-[10px] border border-transparent relative transition-opacity duration-300",
                    "text-brand-forest-500 hover:bg-brand-forest-50 hover:text-brand-forest-700",
                    disabled && "opacity-40 cursor-default",
                    isActive &&
                        "border-brand-forest-200 bg-white text-brand-terra-600 shadow-[0_1px_2px_rgba(27,67,50,0.06)]",
                )}
            >
                <div
                    className={cn(
                        "size-5 flex-shrink-0",
                        isActive && "text-brand-terra-500",
                    )}
                >
                    {icon}
                </div>
                {badge !== undefined && badge > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-forest-600 px-1 text-[10px] font-medium text-white">
                        {badge}
                    </span>
                )}
                {attention && !(badge !== undefined && badge > 0) && (
                    <span
                        className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-brand-terra-600 ring-2 ring-white"
                        data-testid="nav-attention-pip"
                        aria-label="Needs attention"
                    />
                )}
            </div>
        );

        // AIDEV-NOTE: -translate-y-1 aligns tooltip with icon center (alignOffset prop doesn't work)
        if (disabled) {
            return (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div aria-disabled="true">{content}</div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="-translate-y-1">
                        <p>{text}</p>
                    </TooltipContent>
                </Tooltip>
            );
        }

        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <Link to={href} data-tour-id={dataTourId}>
                        {content}
                    </Link>
                </TooltipTrigger>
                <TooltipContent side="right" className="-translate-y-1">
                    <p>{text}</p>
                </TooltipContent>
            </Tooltip>
        );
    }

    if (disabled) {
        return (
            <div
                className="flex w-full items-center justify-end py-1.5 opacity-40 cursor-default"
                aria-disabled="true"
            >
                <div className="flex min-w-[170px] items-center justify-end gap-3 rounded-[10px] border border-transparent px-3 py-2.5 text-brand-forest-700">
                    {attention && (
                        <span
                            className="size-2.5 shrink-0 rounded-full bg-brand-terra-600"
                            data-testid="nav-attention-pip"
                            aria-label="Needs attention"
                        />
                    )}
                    <span>{text}</span>
                    <div className="size-5 flex-shrink-0 text-brand-forest-500">
                        {icon}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Link
            to={href}
            data-tour-id={dataTourId}
            className="flex w-full items-center justify-end py-1.5"
        >
            <div
                className={cn(
                    "flex min-w-[170px] items-center justify-end gap-3 rounded-[10px] border border-transparent px-3 py-2.5 text-brand-forest-700 transition-colors",
                    "hover:bg-brand-forest-50 hover:text-brand-forest-800",
                    isActive &&
                        "border-brand-forest-200 bg-white font-semibold text-brand-forest-800 shadow-[0_1px_2px_rgba(27,67,50,0.06)]",
                )}
            >
                {badge !== undefined && badge > 0 && (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-forest-600 px-1.5 text-xs font-medium text-white">
                        {badge}
                    </span>
                )}
                {attention && (
                    <span
                        className="size-2.5 shrink-0 rounded-full bg-brand-terra-600"
                        data-testid="nav-attention-pip"
                        aria-label="Needs attention"
                    />
                )}
                <span>{text}</span>
                <div
                    className={cn(
                        "size-5 flex-shrink-0 text-brand-forest-500",
                        isActive &&
                            "text-brand-terra-600 [&>svg]:stroke-[2.25]",
                    )}
                >
                    {icon}
                </div>
            </div>
        </Link>
    );
};

interface SidebarNavProps extends React.HTMLAttributes<HTMLDivElement> {
    collapsed?: boolean;
    onToggleCollapsed?: () => void;
    /** Whether to animate transitions. Set to false during initial hydration to prevent flicker. */
    shouldAnimate?: boolean;
    /** Hide the collapse/expand button. Used when secondary sidebar forces collapsed state. */
    hideCollapseButton?: boolean;
}

interface ProtectedLayoutData {
    hasActiveCashAffiliateProgram?: boolean;
}

export function SidebarNav({
    className,
    collapsed = false,
    onToggleCollapsed,
    shouldAnimate = true,
    hideCollapseButton = false,
    ...props
}: SidebarNavProps) {
    const location = useLocation();
    const { isPractitioner } = useIsPractitioner();
    const { isAnonymous } = useAnonymous();
    const { data: userDetails } = useUserDetails();
    const protectedData = useRouteLoaderData("routes/_protected") as
        | ProtectedLayoutData
        | undefined;
    const isAdminRoute = location.pathname.startsWith("/admin");
    const hasActiveCashAffiliateProgram =
        protectedData?.hasActiveCashAffiliateProgram === true;
    // AIDEV-NOTE: Orange pip on the New Session / Add Session nav item when a
    // recording is stranded in this browser's IndexedDB awaiting retry.
    const { count: recoverableRecordingsCount } = useRecoverableRecordings();
    // Launch pip for the Healing Works Academy — see the Resources nav item below.
    const { showPip: showHwaPromoPip } = useHwaPromoPip();
    const hasUnfinishedRecordings = recoverableRecordingsCount > 0;

    const items = useMemo((): NavItemData[] => {
        // If we're on an admin route, show admin navigation
        if (isAdminRoute) {
            return [
                {
                    href: "/admin",
                    icon: <Shield className="size-full" />,
                    text: "Dashboard",
                },
                {
                    href: "/admin/practitioners",
                    icon: <Users className="size-full" />,
                    text: "Practitioners",
                },
                {
                    href: "/admin/feedback",
                    icon: <MessageSquare className="size-full" />,
                    text: "Feedback",
                },
                {
                    href: "/admin/recall-bots",
                    icon: <Mic className="size-full" />,
                    text: "Recall Bots",
                },
                {
                    href: "/admin/event-dispatcher",
                    icon: <Activity className="size-full" />,
                    text: "Event Dispatcher",
                },
            ];
        }

        // Regular navigation for non-admin routes
        const baseItems: NavItemData[] = [];

        const showMeditationsItem =
            userDetails?.showMeditations &&
            userDetails.featureFlags["enable-meditation"];

        if (isPractitioner) {
            // AIDEV-NOTE: Show My Parts menu item only if feature flag is enabled
            baseItems.unshift({
                href: "/parts",
                icon: <Shapes className="size-full" />,
                text: "My Parts",
            });
            // AIDEV-NOTE: Show Meditations only if both self and practitioner enable
            if (showMeditationsItem) {
                baseItems.unshift({
                    href: "/meditations",
                    icon: <Headphones className="size-full" />,
                    text: "Meditations",
                });
            }
            // AIDEV-NOTE: Separate Clients and Sessions main nav items for practitioners.
            // Each has its own secondary sidebar (client list vs sessions sub-nav).
            // "Add Session" and "Add Client" actions live in the secondary sidebar instead of here.
            baseItems.unshift({
                href: "/practitioner/sessions",
                icon: <FileText className="size-full" />,
                text: "Sessions",
            });
            baseItems.unshift({
                href: "/practitioner",
                icon: <Users className="size-full" />,
                text: "Clients",
            });
            // AIDEV-NOTE: Consultant nav item always visible for practitioners
            baseItems.unshift({
                href: "/practitioner/consultant",
                icon: <MessageCircle className="size-full" />,
                text: "Consultant",
            });
            // AIDEV-NOTE: Show Commitments for practitioners if enabled
            if (userDetails?.showCommitments) {
                baseItems.push({
                    href: "/commitments",
                    icon: <CheckSquare className="size-full" />,
                    text: "Commitments",
                    badge: userDetails?.commitmentsCount,
                });
            }
            baseItems.push({
                href: "/resources",
                icon: <BookOpen className="size-full" />,
                text: "Resources",
                // AIDEV-NOTE: One-shot launch pip for the Healing Works Academy. Clears
                // the first time they open Resources (see useHwaPromoPip), so it means
                // "something new is in here" rather than a persistent state.
                attention: showHwaPromoPip,
            });
            if (hasActiveCashAffiliateProgram) {
                baseItems.unshift({
                    href: "/account/affiliate",
                    icon: <Gift className="size-full" />,
                    text: "Affiliate Program",
                });
            }
            baseItems.push({
                href: "/invite",
                icon: <Plus className="size-full" />,
                text: "Add Session",
                attention: hasUnfinishedRecordings,
            });
        } else {
            baseItems.push({
                href: "/invite",
                icon: <Plus className="size-full" />,
                text: "New Session",
                attention: hasUnfinishedRecordings,
            });
            // AIDEV-NOTE: Show Parts menu item first
            baseItems.unshift({
                href: "/parts",
                icon: <Shapes className="size-full" />,
                text: "Parts",
            });

            // AIDEV-NOTE: Show Meditations menu item only if both self and practitioner enable
            if (showMeditationsItem) {
                baseItems.unshift({
                    href: "/meditations",
                    icon: <Headphones className="size-full" />,
                    text: "Meditations",
                });
            }

            // AIDEV-NOTE: Show Sessions menu item
            baseItems.unshift({
                href: "/sessions",
                icon: <FileText className="size-full" />,
                text: "Sessions",
            });

            // AIDEV-NOTE: Show Commitments if enabled (computed server-side)
            if (userDetails?.showCommitments) {
                baseItems.push({
                    href: "/commitments",
                    icon: <CheckSquare className="size-full" />,
                    text: "Commitments",
                    badge: userDetails?.commitmentsCount,
                });
            }

            baseItems.push({
                href: "/resources",
                icon: <BookOpen className="size-full" />,
                text: "Resources",
                // AIDEV-NOTE: One-shot launch pip for the Healing Works Academy. Clears
                // the first time they open Resources (see useHwaPromoPip), so it means
                // "something new is in here" rather than a persistent state.
                attention: showHwaPromoPip,
            });
            if (hasActiveCashAffiliateProgram) {
                baseItems.push({
                    href: "/account/affiliate",
                    icon: <Gift className="size-full" />,
                    text: "Affiliate Program",
                });
            }

            // AIDEV-NOTE: Only add conditional items after hydration to avoid mismatches
            if (!isAnonymous) {
                baseItems.push({
                    href: "/client/invite-practitioner",
                    icon: <UserPlus className="size-full" />,
                    text: "Invite Practitioner",
                });
            }
        }

        // AIDEV-NOTE: No "Consulting" main-nav item — sessions shared with this
        // user as a consultant are just sessions, surfaced by the
        // "I'm the Consultant" filter in the practitioner sessions sub-nav
        // (_protected.practitioner.tsx), the same way "I'm the Client" works.

        return baseItems;
    }, [
        isPractitioner,
        isAnonymous,
        isAdminRoute,
        hasUnfinishedRecordings,
        userDetails?.selfEnabledCommitments,
        userDetails?.practitionerEnabledCommitments,
        userDetails?.commitmentsCount,
        userDetails?.showMeditations,
        userDetails?.showCommitments,
        userDetails?.isConsultant,
        userDetails?.featureFlags,
        hasActiveCashAffiliateProgram,
        // AIDEV-NOTE: Load-bearing. The promo-seen read is ASYNC, so without this dep the
        // pip was pinned to its first (false) value: it could never appear, and markSeen
        // could never clear it once it had.
        showHwaPromoPip,
    ]);

    // AIDEV-NOTE: Logo always links to the user's non-admin home so clicking it
    // from within /admin exits admin rather than reloading the admin shell.
    const logoLink = isPractitioner ? "/practitioner" : "/sessions";

    // AIDEV-NOTE: Collapsed sidebar shows only icons (width ~64px), expanded shows full nav
    return (
        <TooltipProvider delayDuration={0}>
            <div
                className={cn(
                    "border-r border-border min-h-screen bg-white relative text-brand-forest-800",
                    "sticky top-0 z-40 print:hidden",
                    // AIDEV-NOTE: Only animate after initial hydration to prevent flicker on navigation
                    shouldAnimate && "transition-all duration-300 ease-in-out",
                    collapsed
                        ? "w-16 min-w-16"
                        : "md:min-w-[240px] 2xl:min-w-[15%] w-52 2xl:w-72 2xl:pl-4",
                    // AIDEV-NOTE: Keep collapsed navigation fully opaque so icon-only
                    // actions remain easy to discover; expanded navigation may still
                    // fade until hover.
                    isAnonymous || hideCollapseButton || collapsed
                        ? ""
                        : "[&>*]:opacity-50 [&>*]:transition-opacity [&>*]:duration-300 hover:[&>*]:opacity-100",
                    className,
                )}
                {...props}
            >
                {/* AIDEV-NOTE: Collapse toggle button - positioned at top right of sidebar,
                    to the right of the logo. Uses top positioning relative to the sticky container.
                    Hidden when secondary sidebar is present (forceCollapsed state). */}
                {onToggleCollapsed && !hideCollapseButton && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={onToggleCollapsed}
                                aria-label={
                                    collapsed
                                        ? "Expand sidebar"
                                        : "Collapse sidebar"
                                }
                                className={cn(
                                    "absolute top-14 lg:top-[96px] -right-4 z-50",
                                    "flex h-8 w-8 items-center justify-center",
                                    "rounded-full border bg-background shadow-md",
                                    "text-muted-foreground transition-colors hover:text-brand-forest-800 hover:bg-muted",
                                    shouldAnimate &&
                                        "transition-all duration-300 ease-in-out",
                                )}
                                data-analytics-id="sidebar toggle collapse"
                            >
                                {collapsed ? (
                                    <ChevronRight className="size-4" />
                                ) : (
                                    <ChevronLeft className="size-4" />
                                )}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                            <p>
                                {collapsed
                                    ? "Expand sidebar"
                                    : "Collapse sidebar"}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                )}
                <div
                    className={cn(
                        // AIDEV-NOTE: pt-7 / 2xl:pt-11 put the logo's top edge on the first line of
                        // the secondary rail at both breakpoints: Sidebar.Content's pt-5 2xl:pt-9
                        // plus Sidebar.NavItem's py-2 = 28px and 44px. Was pt-6 2xl:pt-16 (24 / 64).
                        "sticky top-0 flex h-screen flex-col space-y-6 py-4 pt-7 2xl:pt-11 overflow-y-hidden overflow-x-visible",
                        collapsed ? "min-w-16 items-center" : "min-w-[240px]",
                    )}
                >
                    {/* AIDEV-NOTE: Logo right edge aligns with nav icon right edge.
                        Collapsed: centered. Expanded: right-aligned within same min-w-[170px] block as nav items.
                        Logo owns its anchor so the admin shell doesn't nest Links during hydration. */}
                    <div
                        className={cn(
                            "flex items-center",
                            collapsed
                                ? "justify-center px-2"
                                : "mx-auto w-auto",
                        )}
                    >
                        {collapsed ? (
                            <Logo size="sm" to={logoLink} variant="image" />
                        ) : (
                            <div className="flex justify-end min-w-[170px]">
                                <Logo size="md" to={logoLink} variant="image" />
                            </div>
                        )}
                    </div>

                    {/* Navigation items */}
                    <div className="flex-1 w-full flex flex-col justify-start">
                        <div
                            className={cn(
                                "space-y-1.5",
                                collapsed ? "px-3" : "mx-auto w-auto",
                            )}
                        >
                            {items.map((item) => {
                                // AIDEV-NOTE: Extended active detection for nested routes.
                                // Practitioner "Clients" stays active for /practitioner/clients/* but not /practitioner/sessions*.
                                // Practitioner "Sessions" stays active for /practitioner/sessions* and /sessions/:id/*.
                                // Client "Sessions" stays active for /sessions/:id/*.
                                const isActive = (() => {
                                    if (location.pathname === item.href)
                                        return true;
                                    // AIDEV-NOTE: Consultant active on /practitioner/consultant/*
                                    if (
                                        item.href ===
                                            "/practitioner/consultant" &&
                                        location.pathname.startsWith(
                                            "/practitioner/consultant",
                                        )
                                    )
                                        return true;
                                    // Practitioner Clients: active on /practitioner/* except /practitioner/sessions*, /practitioner/add-session, /practitioner/consultant*
                                    if (
                                        item.href === "/practitioner" &&
                                        location.pathname.startsWith(
                                            "/practitioner/",
                                        ) &&
                                        !location.pathname.startsWith(
                                            "/practitioner/sessions",
                                        ) &&
                                        !location.pathname.startsWith(
                                            "/practitioner/consultant",
                                        ) &&
                                        location.pathname !==
                                            "/practitioner/add-session"
                                    )
                                        return true;
                                    // Practitioner Sessions: active on /practitioner/sessions/*, /sessions/:id/*, /consultation/* (formerly /reflections/*), and /invite
                                    if (
                                        item.href ===
                                            "/practitioner/sessions" &&
                                        location.pathname.startsWith(
                                            "/sessions/",
                                        )
                                    )
                                        return true;
                                    if (
                                        item.href ===
                                            "/practitioner/sessions" &&
                                        location.pathname.startsWith(
                                            "/consultation/",
                                        )
                                    )
                                        return true;
                                    if (
                                        item.href ===
                                            "/practitioner/sessions" &&
                                        location.pathname.startsWith(
                                            "/practitioner/sessions/",
                                        )
                                    )
                                        return true;
                                    if (
                                        item.href ===
                                            "/practitioner/sessions" &&
                                        location.pathname ===
                                            "/practitioner/add-session"
                                    )
                                        return true;
                                    // Client Sessions: active on /sessions/:id/*
                                    if (
                                        item.href === "/sessions" &&
                                        location.pathname.startsWith(
                                            "/sessions/",
                                        )
                                    )
                                        return true;
                                    if (
                                        item.href === "/parts" &&
                                        location.pathname.startsWith("/parts/")
                                    )
                                        return true;
                                    if (
                                        item.href === "/help" &&
                                        location.pathname.startsWith("/help/")
                                    )
                                        return true;
                                    if (
                                        item.href === "/resources" &&
                                        location.pathname.startsWith(
                                            "/resources/",
                                        )
                                    )
                                        return true;
                                    return false;
                                })();
                                return (
                                    <NavItem
                                        key={item.href}
                                        href={item.href}
                                        isActive={isActive}
                                        icon={item.icon}
                                        text={item.text}
                                        badge={item.badge}
                                        attention={item.attention}
                                        disabled={isAnonymous}
                                        collapsed={collapsed}
                                        data-tour-id={
                                            item.href === "/help"
                                                ? "help-center-nav"
                                                : undefined
                                        }
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer section */}
                    <div className="flex flex-col space-y-2 pb-4">
                        {isPractitioner && !isAnonymous && (
                            <div
                                className={cn(
                                    "flex pb-4",
                                    collapsed
                                        ? "justify-center px-2"
                                        : "justify-end pr-8",
                                )}
                            >
                                {collapsed ? (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                asChild
                                                variant="ghost"
                                                size="icon"
                                                className="size-10 rounded-full border border-brand-terra-200 bg-brand-terra-50 text-brand-terra-700 shadow-[0_1px_2px_rgba(151,61,42,0.08)] hover:bg-brand-terra-100 hover:text-brand-terra-800"
                                                analyticsId="sidebar invite collapsed"
                                            >
                                                <Link
                                                    to="/practitioner/connect?mode=referral"
                                                    aria-label="Invite"
                                                >
                                                    <UserPlus className="size-4" />
                                                </Link>
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                            <p>Invite</p>
                                        </TooltipContent>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        asChild
                                        variant="ghost"
                                        analyticsId="sidebar invite"
                                        className="w-auto justify-start rounded-full border border-brand-forest-200 bg-white px-4 py-2.5 text-brand-forest-800 shadow-[0_1px_2px_rgba(27,67,50,0.06),0_8px_18px_-14px_rgba(27,67,50,0.35)] transition-all hover:border-brand-terra-200 hover:bg-brand-terra-50 hover:text-brand-terra-700"
                                    >
                                        <Link to="/practitioner/connect?mode=referral">
                                            <span className="mr-2 flex size-6 items-center justify-center rounded-full bg-brand-terra-100 text-brand-terra-700">
                                                <UserPlus className="size-3.5" />
                                            </span>
                                            Invite
                                        </Link>
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Practitioner usage meter */}
                        {isPractitioner && (
                            <div
                                className={cn(
                                    "flex pb-2",
                                    collapsed
                                        ? "justify-center px-2"
                                        : "justify-end pr-8",
                                )}
                            >
                                {collapsed ? (
                                    <SessionUsageSidebarCard collapsed />
                                ) : (
                                    <div className="min-w-[170px]">
                                        <SessionUsageSidebarCard />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Help icon (collapsed only, above avatar) */}
                        {collapsed && (
                            <div className="flex justify-center px-2 pb-1">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Link
                                            to="/help"
                                            data-tour-id="help-center-nav"
                                        >
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="size-10"
                                                analyticsId="sidebar help collapsed"
                                                aria-label="Help center"
                                            >
                                                <HelpCircle className="size-5" />
                                            </Button>
                                        </Link>
                                    </TooltipTrigger>
                                    <TooltipContent side="right">
                                        <p>Help center</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        )}

                        {/* User section above divider */}
                        {isAnonymous ? (
                            <div
                                className={cn(
                                    "flex",
                                    collapsed
                                        ? "justify-center px-2 pb-2"
                                        : "justify-end pr-8 pb-2",
                                )}
                            >
                                {collapsed ? (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Link to="/link-account">
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="size-10"
                                                    analyticsId="sidebar create account collapsed"
                                                    aria-label="Create an account"
                                                >
                                                    <UserPlus className="size-4" />
                                                </Button>
                                            </Link>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                            <p>Create an account</p>
                                        </TooltipContent>
                                    </Tooltip>
                                ) : (
                                    <Link to="/link-account">
                                        <Button
                                            variant="outline"
                                            analyticsId="sidebar create account"
                                        >
                                            Create an account
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <div
                                className={cn(
                                    "flex",
                                    collapsed
                                        ? "justify-center px-2 pb-2"
                                        : "justify-end pr-8 pb-2",
                                )}
                            >
                                <UserMenu collapsed={collapsed} />
                            </div>
                        )}

                        {/* AIDEV-NOTE: Expanded footer links share pr-8 with the action row so their right edge stays aligned. */}
                        {!collapsed && (
                            <div className="border-t border-border pt-4 pr-8 text-xs text-muted-foreground">
                                <div className="flex items-center justify-end space-x-2">
                                    <A
                                        href="/terms"
                                        className="transition-colors hover:text-brand-terra-500"
                                    >
                                        Terms
                                    </A>
                                    <Dot className="size-4" />
                                    <A
                                        href="/privacy"
                                        className="transition-colors hover:text-brand-terra-500"
                                    >
                                        Privacy
                                    </A>
                                    <Dot className="size-4" />
                                    <A
                                        href="/help"
                                        className="transition-colors hover:text-brand-terra-500"
                                        data-tour-id="help-center-nav"
                                    >
                                        Support
                                    </A>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
}

// Add SessionsHeader component definition
export interface SessionsHeaderProps {
    selectedDate: Date;
    onDateChange: (date: Date) => void;
    onNewSession: () => void;
    onFilterToggle: () => void;
    onSortChange: () => void;
    sortDirection: "asc" | "desc";
}

export function SessionsHeader({
    selectedDate,
    onDateChange: _onDateChange,
    onNewSession,
    onFilterToggle,
    onSortChange,
    sortDirection,
}: SessionsHeaderProps) {
    return (
        <div className="mb-6">
            {/* For desktop: date picker and actions in the same row */}
            <div className="hidden items-center justify-between md:flex">
                <div>
                    <H2 className="mb-2 text-xl font-semibold">Select Date</H2>
                    <div className="w-[240px] rounded-md border p-2">
                        {selectedDate
                            ? selectedDate.toLocaleDateString()
                            : "Pick a date"}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        onClick={onNewSession}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        <Plus className="size-4" />
                        <span>New Session</span>
                    </Button>

                    <Button
                        onClick={onFilterToggle}
                        variant="outline"
                        size="icon"
                    >
                        <Filter className="size-4" />
                        <span className="sr-only">Filter sessions</span>
                    </Button>

                    <Button
                        onClick={onSortChange}
                        variant="outline"
                        size="icon"
                    >
                        {sortDirection === "asc" ? (
                            <SortAsc className="size-4" />
                        ) : (
                            <SortDesc className="size-4" />
                        )}
                        <span className="sr-only">Sort sessions</span>
                    </Button>
                </div>
            </div>

            <div className="flex flex-col items-center justify-between md:hidden">
                <H2 className="mb-2 text-xl font-semibold">Select Date</H2>
                <div className="mb-4">
                    <div className="w-full rounded-md border p-2">
                        {selectedDate
                            ? selectedDate.toLocaleDateString()
                            : "Pick a date"}
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button
                        onClick={onNewSession}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        <Plus className="size-4" />
                        <span>New Session</span>
                    </Button>

                    <Button
                        onClick={onFilterToggle}
                        variant="outline"
                        size="icon"
                    >
                        <Filter className="size-4" />
                        <span className="sr-only">Filter sessions</span>
                    </Button>

                    <Button
                        onClick={onSortChange}
                        variant="outline"
                        size="icon"
                    >
                        {sortDirection === "asc" ? (
                            <SortAsc className="size-4" />
                        ) : (
                            <SortDesc className="size-4" />
                        )}
                        <span className="sr-only">Sort sessions</span>
                    </Button>
                </div>
            </div>
        </div>
    );
}
